import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type { DeleteRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DeleteRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export const useDeleteRisk = createMutationHook({
  name: 'deleteRisk',
  // risk.delete is single-gated through runWithApprovalWorkflow; the governed
  // path surfaces the same confirm modal as the bulk-delete hooks and replays
  // with _confirmChangeRequest.
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.risk.delete.mutationOptions({ onSuccess }),
  mapInput: (id: string) => ({ id }),
  refreshChangeRequestFeedsWhen: (result: ApprovalGatedResult<unknown>) =>
    result.status !== 'applied',
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed `riskCount` and
    // `appetiteCount` (both left-nav badges) and `risksByTier`.
    trpc.frontend.risk.pathKey(),
    // COI-471 parity: Apollo twin also evicts `acceptance`, `appetite` and
    // `impact_rating` (all hang off the deleted risk).
    trpc.frontend.acceptance.pathKey(),
    trpc.frontend.appetite.pathKey(),
    trpc.frontend.impactRating.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  // trpc throws on 0 affected rows; on the change-request branch no row is
  // deleted yet (a CR was raised) but the action still "succeeded" from the
  // caller's perspective, mirroring the legacy delete flow.
  mapResponse: (): DeleteRiskMutation => ({
    deleteRiskById: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: DeleteRiskDocument,
  graphqlVariables: (id: string) => ({ id }),
  evictFields: [
    'risk',
    'risk_aggregate',
    'acceptance',
    'appetite',
    'appetite_aggregate',
    'impact_rating',
  ],
  refetchQueries: [
    namedOperations.Query.getRiskById,
    namedOperations.Query.getRisksByTier,
  ],
});
